import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IntegrationsPortalPublicationsService,
  type IntegrationActivityEvent,
  type PropertyPortalPublication,
  type PropertyPortalPublicationDisplayStatusFilter,
  type PropertyPortalPublicationEnqueuedActionResponse,
} from 'src/api/generated';
import { type ApiResponse } from 'src/hooks/api/api';
import { useIntegrationActivityEvents } from './integrations';

interface UsePortalPublicationsParams {
  filterBy?: string;
  searchBy?: string;
  sortBy?: string;
  displayStatus?: PropertyPortalPublicationDisplayStatusFilter;
  pageSize?: number;
  pageToken?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

interface UsePortalPublicationsResponse extends ApiResponse {
  publications: PropertyPortalPublication[];
  totalCount?: number;
  nextPageToken?: string;
}

interface UsePortalPublicationEventsResponse extends ApiResponse {
  events: IntegrationActivityEvent[];
}

interface UseEnqueuePortalPublicationActionResponse {
  enqueuePersistedAction: (
    publicationId: number
  ) => Promise<PropertyPortalPublicationEnqueuedActionResponse>;
  loading: boolean;
  error: Error | null;
}

export const usePortalPublications = ({
  filterBy,
  searchBy,
  sortBy,
  displayStatus,
  pageSize = 20,
  pageToken,
  enabled = true,
  refetchInterval = false,
}: UsePortalPublicationsParams): UsePortalPublicationsResponse => {
  const fetcher = async () => {
    return IntegrationsPortalPublicationsService.listPublications(
      filterBy,
      searchBy,
      sortBy,
      undefined,
      pageSize,
      pageToken,
      displayStatus
    );
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: [
      'integrations',
      'portal-publications',
      filterBy,
      searchBy,
      sortBy,
      displayStatus,
      pageToken,
      pageSize,
    ],
    queryFn: fetcher,
    enabled,
    refetchInterval,
  });

  return {
    loading,
    error,
    refetch,
    publications: data?.items ?? [],
    totalCount: data?.totalCount,
    nextPageToken: data?.nextPageToken,
  };
};

export const usePortalPublicationEvents = (
  publicationId: number | null | undefined,
  enabled = true,
  refetchInterval: number | false = false
): UsePortalPublicationEventsResponse => {
  const { events, loading, error, refetch } = useIntegrationActivityEvents(
    {
      propertyPortalPublicationId: publicationId ?? undefined,
      pageSize: 100,
    },
    enabled && publicationId != null,
    refetchInterval
  );

  return {
    loading,
    error,
    refetch,
    events,
  };
};

const useEnqueuePortalPublicationAction = (
  mutationKey: string[],
  mutationFn: (
    publicationId: number
  ) => Promise<PropertyPortalPublicationEnqueuedActionResponse>
): UseEnqueuePortalPublicationActionResponse => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation({
    mutationKey,
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['integrations', 'portal-publications'],
      });
    },
  });

  return {
    enqueuePersistedAction: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

export const useEnqueuePortalPublicationPause =
  (): UseEnqueuePortalPublicationActionResponse =>
    useEnqueuePortalPublicationAction(
      ['integrations', 'portal-publications', 'pause', 'enqueue'],
      async (publicationId) =>
        IntegrationsPortalPublicationsService.enqueuePublicationPause(
          publicationId
        )
    );

export const useEnqueuePortalPublicationPublish =
  (): UseEnqueuePortalPublicationActionResponse =>
    useEnqueuePortalPublicationAction(
      ['integrations', 'portal-publications', 'publish', 'enqueue'],
      async (publicationId) =>
        IntegrationsPortalPublicationsService.enqueuePublicationPublish(
          publicationId
        )
    );

export const useEnqueuePortalPublicationUnpublish =
  (): UseEnqueuePortalPublicationActionResponse =>
    useEnqueuePortalPublicationAction(
      ['integrations', 'portal-publications', 'unpublish', 'enqueue'],
      async (publicationId) =>
        IntegrationsPortalPublicationsService.enqueuePublicationUnpublish(
          publicationId
        )
    );
